import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    FormControlLabel,
    Checkbox,
    Select,
    MenuItem,
    InputLabel,
    Box,
    CircularProgress,
    IconButton,
    Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/system';
import { getAutoFillSourceQuestionsAPI, updateAutoFillSettingsAPI } from '../../../../../services/api/question.api';
import { QUESTION_INPUT_TYPE, QUESTION_TYPE_OPTIONS } from '../../../../../types/question.type';
import { PAGE_TYPE } from '../../../../../types/pages.type';
import useFacilityId from '../../../../../hooks/useFacilityId';

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
}));

const AutoFillSettingsDialog = ({
    open,
    onClose,
    question,
    onSave,
    latestType,
    transferType
}) => {
    // Auto-fill functionality states
    const [enableAutoFill, setEnableAutoFill] = useState(false);
    const [sourceTransitionType, setSourceTransitionType] = useState('');
    const [sourceQuestionId, setSourceQuestionId] = useState('');
    const [availableSourceQuestions, setAvailableSourceQuestions] = useState([]);
    const [loadingSourceQuestions, setLoadingSourceQuestions] =/*  */ useState(false);
    const { selectedAccount } = useSelector((state) => state.common);
    const dashboardAccess = useMemo(() => {
        return selectedAccount &&
            selectedAccount.dashboardAccess &&
            selectedAccount.dashboardAccess.length > 0
            ? selectedAccount.dashboardAccess
            : [];
    }, [selectedAccount]);
    const { facilityId } = useFacilityId();
    // Define transition type options for auto-fill - filtered by dashboard access
    const transitionTypeOptions = useMemo(() => {
        const options = [
            { value: 'admission', label: 'Admission/readmission', forType: 'admission', pageType: PAGE_TYPE.ADMISSION },
            { value: 'return', label: 'Return', forType: 'return', pageType: PAGE_TYPE.ADMISSION },
            { value: 'hospitalTransfer', label: 'Hospital Transfer', forType: 'transfer', forTransferType: 'hospitalTransfer', pageType: PAGE_TYPE.HOSPITAL },
            { value: 'safeDischarge', label: 'Safe Discharge', forType: 'transfer', forTransferType: 'safeDischarge', pageType: PAGE_TYPE.COMMUNITY_TRANSFER },
            { value: 'SNF', label: 'SNF', forType: 'transfer', forTransferType: 'SNF', pageType: PAGE_TYPE.COMMUNITY_TRANSFER },
            { value: 'AMA', label: 'AMA', forType: 'transfer', forTransferType: 'AMA', pageType: PAGE_TYPE.COMMUNITY_TRANSFER },
            { value: 'deceased', label: 'Deceased', forType: 'transfer', forTransferType: 'deceased', pageType: PAGE_TYPE.DECEASED },
        ];

        if (_.includes(dashboardAccess, PAGE_TYPE.OVERALL)) {
            return options;
        }

        return options.filter(option => _.includes(dashboardAccess, option.pageType));
    }, [dashboardAccess]);

    // Fetch source questions for auto-fill
    const fetchSourceQuestions = async (transitionType) => {
        if (!transitionType) return;

        setLoadingSourceQuestions(true);
        try {
            const params = {
                facilityid: facilityId,
                sourceTransitionType: transitionType,
                targetQuestionType: question.customQuestionInputType,
                questionId: question?._id,
                isCustom: question?.isCustom,
                questionType: question?.type,
                multiple: question?.multiple,
                validationType: question?.validationType
            };

            const response = await getAutoFillSourceQuestionsAPI(params);
            setAvailableSourceQuestions(response || []);
        } catch (error) {
            console.error('Error fetching source questions:', error);
            setAvailableSourceQuestions([]);
        } finally {
            setLoadingSourceQuestions(false);
        }
    };

    // Handle transition type change
    const handleTransitionTypeChange = (value) => {
        setSourceTransitionType(value);
        setSourceQuestionId(''); // Reset selected question
        fetchSourceQuestions(value);
    };

    // Handle auto-fill toggle
    const handleAutoFillToggle = (checked) => {
        setEnableAutoFill(checked);
        if (!checked) {
            // Reset auto-fill related fields
            setSourceTransitionType('');
            setSourceQuestionId('');
            setAvailableSourceQuestions([]);
        }
    };

    // Initialize auto-fill states when editing an existing question
    useEffect(() => {
        if (question) {

            // Check if auto-fill is enabled - backend stores directly on question object
            const autoFillEnabled = question.autoFillEnabled || false;
            const sourceTransitionType = question.sourceTransitionType || '';
            const sourceQuestionId = question.sourceQuestionId || '';

            // Always set the states based on the question data
            setEnableAutoFill(autoFillEnabled);
            setSourceTransitionType(sourceTransitionType);
            setSourceQuestionId(sourceQuestionId);

            // If auto-fill is enabled and has transition type, fetch source questions
            if (autoFillEnabled && sourceTransitionType) {
                fetchSourceQuestions(sourceTransitionType);
            } else {
                setAvailableSourceQuestions([]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [question]);

    const handleSave = async () => {
        try {
            const autoFillData = {
                autoFillEnabled: enableAutoFill,
                sourceTransitionType: enableAutoFill ? sourceTransitionType : '',
                sourceQuestionId: enableAutoFill ? sourceQuestionId : ''
            };
            await updateAutoFillSettingsAPI(question._id, autoFillData);
            if (onSave) {
                onSave(autoFillData);
            }
            onClose();
        } catch (error) {
            console.error('Error saving auto-fill settings:', error);
        }
    };

    const handleClose = () => {
        if (question) {
            const autoFillEnabled = question.autoFillEnabled || false;
            const sourceTransitionType = question.sourceTransitionType || '';
            const sourceQuestionId = question.sourceQuestionId || '';

            setEnableAutoFill(autoFillEnabled);
            setSourceTransitionType(sourceTransitionType);
            setSourceQuestionId(sourceQuestionId);

            if (autoFillEnabled && sourceTransitionType) {
                fetchSourceQuestions(sourceTransitionType);
            } else {
                setAvailableSourceQuestions([]);
            }
        }
        onClose();
    };

    if (!question) return null;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <StyledDialogTitle>
                Auto-Fill Settings for "{question.label}"
                <IconButton aria-label="close" onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </StyledDialogTitle>
            <Divider />
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={enableAutoFill}
                                onChange={(e) => handleAutoFillToggle(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Enable auto-fill from existing questions"
                    />
                    {enableAutoFill && (
                        <Box sx={{ mt: 2, pl: 4 }}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel id="transition-type-label">Source Transition Type</InputLabel>
                                <Select
                                    labelId="transition-type-label"
                                    value={sourceTransitionType || ''}
                                    onChange={(e) => handleTransitionTypeChange(e.target.value)}
                                    label="Source Transition Type"
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            backgroundColor: 'white',
                                            padding: '0 4px'
                                        }
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>Select a transition type</em>
                                    </MenuItem>
                                    {transitionTypeOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {sourceTransitionType && (
                                <FormControl fullWidth margin="normal">
                                    <InputLabel id="source-question-label">Source Question</InputLabel>
                                    <Select
                                        labelId="source-question-label"
                                        value={sourceQuestionId || ''}
                                        onChange={(e) => setSourceQuestionId(e.target.value)}
                                        label="Source Question"
                                        disabled={loadingSourceQuestions}
                                        sx={{
                                            '& .MuiInputLabel-root': {
                                                backgroundColor: 'white',
                                                padding: '0 4px'
                                            }
                                        }}
                                    >
                                        {loadingSourceQuestions ? (
                                            <MenuItem value="">
                                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                                Loading questions...
                                            </MenuItem>
                                        ) : availableSourceQuestions.length === 0 ? (
                                            <MenuItem value="">
                                                <em>No compatible questions found</em>
                                            </MenuItem>
                                        ) : [
                                            <MenuItem key="select-placeholder" value="">
                                                <em>Select a source question</em>
                                            </MenuItem>,
                                            ...availableSourceQuestions.map((sourceQuestion) => (
                                                <MenuItem
                                                    key={sourceQuestion._id}
                                                    value={sourceQuestion._id}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        whiteSpace: 'normal'
                                                    }}
                                                >
                                                    <span style={{ flex: 1 }}>{sourceQuestion.label}</span>
                                                    {sourceQuestion.customQuestionInputType && (
                                                        <span style={{
                                                            color: '#666',
                                                            fontSize: '0.8em',
                                                            flexShrink: 0
                                                        }}>
                                                            ({QUESTION_TYPE_OPTIONS.find(
                                                                (o) => o.value === sourceQuestion.customQuestionInputType
                                                            )?.label ?? sourceQuestion.customQuestionInputType})
                                                        </span>
                                                    )}
                                                </MenuItem>
                                            ))
                                        ]}
                                    </Select>
                                </FormControl>
                            )}

                            {sourceTransitionType && availableSourceQuestions.length === 0 && !loadingSourceQuestions && (
                                <Box sx={{ mt: 1, p: 1, backgroundColor: '#fff3cd', borderRadius: 1 }}>
                                    <span style={{ color: '#856404', fontSize: '0.9em' }}>
                                        ❌ No compatible questions found for auto-fill.
                                        {question.customQuestionInputType === QUESTION_INPUT_TYPE.NUMBER_RANGE &&
                                            ' Number questions can only pull from other number questions.'}
                                        {question.customQuestionInputType === QUESTION_INPUT_TYPE.DATE &&
                                            ' Date questions can only pull from other date questions.'}
                                    </span>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="secondary">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    variant="contained"
                    disabled={enableAutoFill && (!sourceTransitionType || !sourceQuestionId)}
                >
                    Save Auto-Fill Settings
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AutoFillSettingsDialog;
